import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { SeriesPoint } from '../api/types';

export interface TrendChartProps {
  series: SeriesPoint[] | null;
  granularity: 'day' | 'week' | 'month';
}

const GRANULARITY_LABEL: Record<TrendChartProps['granularity'], string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
};

/** Formats a `bucket_start` ISO date for the axis, scoped to the granularity. */
function formatTick(value: string, granularity: TrendChartProps['granularity']): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  if (granularity === 'month') {
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

interface TrendTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: SeriesPoint }>;
  granularity: TrendChartProps['granularity'];
}

/** Values lead, the date follows — the reader already has the axis position. */
function TrendTooltip({ active, payload, granularity }: TrendTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-white font-semibold tabular-nums">
        {p.friendships_formed.toLocaleString()} friendships
      </div>
      <div className="text-gray-400 mt-0.5">{formatTick(p.bucket_start, granularity)}</div>
    </div>
  );
}

/**
 * Friendships formed over time. A null `series` means the query failed — this
 * is never drawn as a flat line at zero, which would assert "nothing formed"
 * when the truth is "we could not find out." An empty (non-null) array is a
 * different, honest claim: the query ran and found no activity in range.
 */
export function TrendChart({ series, granularity }: TrendChartProps): React.ReactElement {
  if (series === null) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-gray-500">
        Not available
      </div>
    );
  }

  if (series.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-sm text-gray-500">
        No friendships formed in this range.
      </div>
    );
  }

  const lastIndex = series.length - 1;

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 12, right: 16, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="friendTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.12} />
              <stop offset="100%" stopColor="#a78bfa" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1f2937" strokeDasharray="0" vertical={false} />
          <XAxis
            dataKey="bucket_start"
            tickFormatter={(v: string) => formatTick(v, granularity)}
            stroke="#6b7280"
            fontSize={11}
            minTickGap={32}
            label={{
              value: `Per ${GRANULARITY_LABEL[granularity].toLowerCase()}`,
              position: 'insideBottomRight',
              fill: '#6b7280',
              fontSize: 11,
              offset: -4,
            }}
          />
          <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} width={40} />
          <Tooltip
            content={<TrendTooltip granularity={granularity} />}
            cursor={{ stroke: '#a78bfa', strokeOpacity: 0.4 }}
          />
          <Area
            type="monotone"
            dataKey="friendships_formed"
            stroke="#a78bfa"
            strokeWidth={2}
            fill="url(#friendTrendFill)"
            isAnimationActive={false}
            dot={false}
            activeDot={{ r: 4, fill: '#a78bfa', stroke: '#030712', strokeWidth: 2 }}
            label={(props: {
              x?: number | string;
              y?: number | string;
              index?: number;
              value?: number | string | boolean | null;
            }) => {
              const { x, y, index, value } = props;
              const nx = typeof x === 'string' ? parseFloat(x) : x;
              const ny = typeof y === 'string' ? parseFloat(y) : y;
              if (index !== lastIndex || nx === undefined || ny === undefined) return <g />;
              const nv = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : 0);
              return (
                <text
                  x={nx}
                  y={ny - 10}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#e5e7eb"
                  className="tabular-nums"
                >
                  {nv.toLocaleString()}
                </text>
              );
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TrendChart;
